import React, { useState, useEffect, useRef } from 'react';
import { getOptimizedImageUrl } from '../../utils/CloudinaryAPI';
import './LogoManagement.css';

// Cloudinary Upload Widget types
interface CloudinaryUploadWidgetResults {
  event: string;
  info: {
    secure_url?: string;
    url?: string;
    public_id?: string;
    width?: number;
    height?: number;
    [key: string]: any; // Allow additional properties
  };
}

// Type for Cloudinary widget
type CloudinaryWidget = {
  open: () => void;
  close: () => void;
};

interface LogoData {
  imageUrl: string;
  altText: string;
  publicId?: string;
  width?: number;
  height?: number;
}

const LogoManagement: React.FC = () => {
  const [logo, setLogo] = useState<LogoData>({
    imageUrl: '',
    altText: 'Carmen Lorraine Photography'
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [showUploadWidget, setShowUploadWidget] = useState(false);
  const [cloudinaryLoaded, setCloudinaryLoaded] = useState(false);
  const uploadWidgetRef = useRef<CloudinaryWidget | null>(null);

  // Helper function to get CSS variable value
  const getCSSVariable = (variableName: string, fallback: string = '#8B4513'): string => {
    if (typeof window === 'undefined') return fallback;
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(variableName).trim();
    return value || fallback;
  };

  useEffect(() => {
    loadLogo();
    loadCloudinaryScript();
  }, []);

  const loadCloudinaryScript = () => {
    // Check if Cloudinary is already loaded
    if ((window as any).cloudinary) {
      setCloudinaryLoaded(true);
      return;
    }

    // Load Cloudinary script
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    script.onload = () => {
      setCloudinaryLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Cloudinary script');
      setUploadError('Failed to load Cloudinary. Please refresh the page.');
    };
    document.head.appendChild(script);
  };

  const loadLogo = () => {
    const logoData = localStorage.getItem('website_logo');
    if (logoData) {
      try {
        const logoParsed = JSON.parse(logoData);
        setLogo(prev => ({ ...prev, ...logoParsed }));
      } catch (error) {
        console.error('Error loading logo:', error);
      }
    }
  };

  const saveLogo = (logoData: LogoData) => {
    try {
      // Validate logo data
      if (!logoData.imageUrl || logoData.imageUrl.trim() === '') {
        console.warn('Attempting to save logo with empty imageUrl');
      }
      
      const logoJson = JSON.stringify(logoData);
      localStorage.setItem('website_logo', logoJson);
      
      // Verify it was saved
      const saved = localStorage.getItem('website_logo');
      if (!saved || saved !== logoJson) {
        throw new Error('Failed to save logo to localStorage');
      }
      
      setLogo(logoData);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('logo_updated', { detail: logoData }));
      
      // Also dispatch a storage event to trigger cross-tab updates
      try {
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'website_logo',
          newValue: logoJson,
          oldValue: localStorage.getItem('website_logo'),
          storageArea: localStorage
        }));
      } catch (e) {
        // StorageEvent might not work in all browsers, that's okay
      }
      
      setUploadSuccess('Logo updated successfully!');
      setTimeout(() => setUploadSuccess(null), 3000);
      setUploadError(null);
    } catch (error) {
      console.error('❌ Error saving logo:', error);
      setUploadError(`Failed to save logo: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setUploadSuccess(null);
    }
  };

  const handleUploadSuccess = (result: CloudinaryUploadWidgetResults) => {
    try {
      // Handle different result formats from Cloudinary
      let imageUrl = '';
      let publicId = '';
      let width = 0;
      let height = 0;
      
      if (result.info) {
        // Standard format
        if (typeof result.info === 'object') {
          imageUrl = result.info.secure_url || result.info.url || '';
          publicId = result.info.public_id || '';
          width = result.info.width || 0;
          height = result.info.height || 0;
        }
      } else if ((result as any).secure_url || (result as any).url) {
        // Direct format (result itself contains the data)
        imageUrl = (result as any).secure_url || (result as any).url || '';
        publicId = (result as any).public_id || '';
        width = (result as any).width || 0;
        height = (result as any).height || 0;
      }
      
      if (!imageUrl) {
        console.error('No image URL found in result:', result);
        setUploadError('Upload succeeded but no image URL was returned. Please try again.');
        setIsUploading(false);
        setShowUploadWidget(false);
        return;
      }
      
      const newLogo: LogoData = {
        imageUrl: imageUrl,
        altText: logo.altText || 'Carmen Lorraine Photography',
        publicId: publicId,
        width: width,
        height: height
      };
      
      saveLogo(newLogo);
      setIsUploading(false);
      setShowUploadWidget(false);
      setUploadError(null);
    } catch (error) {
      console.error('Error processing upload result:', error);
      setUploadError('Error processing uploaded image. Please try again.');
      setIsUploading(false);
      setShowUploadWidget(false);
    }
  };

  const handleUploadError = (error: any) => {
    console.error('Upload error:', error);
    setUploadError('Failed to upload logo. Please try again.');
    setIsUploading(false);
    setShowUploadWidget(false);
  };

  const openUploadWidget = () => {
    if (!cloudinaryLoaded) {
      setUploadError('Cloudinary is still loading. Please wait a moment and try again.');
      return;
    }
    
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    
    if (uploadWidgetRef.current) {
      uploadWidgetRef.current.open();
    } else {
      // Try different upload presets in order of preference
      const uploadPresets = ['unsigned_upload', 'carmen_lorraine_photos', 'ml_default'];
      const selectedPreset = uploadPresets[0]; // Start with unsigned_upload
      
      // Get Cloudinary cloud name - use the same default as cloudinary-config.ts
      const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'di1mzmlts';
      
      // Create upload widget configuration
      const widgetConfig: any = {
        cloudName: cloudName,
        uploadPreset: selectedPreset,
        folder: 'carmen_lorraine_photos/logos',
        maxFiles: 1,
        resourceType: 'image',
        cropping: true,
        croppingAspectRatio: 1,
        croppingShowDimensions: true,
        multiple: false,
        sources: ['local', 'url'],
        maxImageWidth: 2000,
        maxImageHeight: 2000,
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        theme: 'minimal',
        styles: {
          palette: {
            window: "#FFFFFF",
            sourceBg: "#F4F4F5",
            windowBorder: "#90a0b3",
            tabIcon: getCSSVariable('--color-primary', '#8B4513'),
            inactiveTabIcon: "#69778a",
            menuIcons: getCSSVariable('--color-primary', '#8B4513'),
            link: getCSSVariable('--color-primary', '#8B4513'),
            action: getCSSVariable('--color-primary', '#8B4513'),
            inProgress: getCSSVariable('--color-primary', '#8B4513'),
            complete: "#20b832",
            error: "#ea2727",
            textDark: "#000000",
            textLight: "#FFFFFF"
          }
        }
      };
      
      console.log('Widget configuration:', widgetConfig);
      
      // Create upload widget using window.cloudinary (typed as any to avoid conflicts)
      const cloudinaryWidget = (window as any).cloudinary?.createUploadWidget(
        widgetConfig,
        (error: any, result: any) => {
          console.log('Cloudinary widget callback:', { error, result });
          
          if (error) {
            console.error('Cloudinary upload error:', error);
            handleUploadError(error);
            return;
          }
          
          if (!result) {
            console.warn('No result from Cloudinary widget');
            return;
          }
          
          // Handle different event types
          if (result.event === 'success') {
            console.log('Upload successful, processing result...');
            handleUploadSuccess(result as CloudinaryUploadWidgetResults);
          } else if (result.event === 'close') {
            console.log('Upload widget closed');
            setIsUploading(false);
            setShowUploadWidget(false);
          } else if (result.event === 'abort') {
            console.log('Upload aborted');
            setIsUploading(false);
            setShowUploadWidget(false);
          } else if (result.event === 'display-changed') {
            console.log('Upload widget display changed');
            // Don't change state for display changes
          } else {
            console.log('Other Cloudinary event:', result.event);
          }
        }
      );
      
      if (cloudinaryWidget) {
        console.log('Upload widget created successfully');
        uploadWidgetRef.current = cloudinaryWidget as CloudinaryWidget;
        cloudinaryWidget.open();
      } else {
        console.error('Failed to create upload widget');
        setUploadError('Cloudinary upload widget not available. Please refresh the page and try again.');
        setIsUploading(false);
      }
    }
  };

  const removeLogo = () => {
    const emptyLogo: LogoData = {
      imageUrl: '',
      altText: 'Carmen Lorraine Photography'
    };
    saveLogo(emptyLogo);
  };

  const updateAltText = (altText: string) => {
    const updatedLogo = { ...logo, altText };
    saveLogo(updatedLogo);
  };

  return (
    <section className="admin-section logo-management">
      <div className="section-header">
        <h2>Logo</h2>
        <p>Upload and manage your website logo</p>
      </div>

      <div className="section-content">
        {uploadError && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-triangle"></i>
            {uploadError}
          </div>
        )}

        {uploadSuccess && (
          <div className="alert alert-success">
            <i className="fas fa-check-circle"></i>
            {uploadSuccess}
          </div>
        )}

        <div className="logo-management-grid">
          {/* Current Logo Display */}
          <div className="logo-preview-section">
            <h3>Current Logo</h3>
            <div className="logo-preview-container">
              {logo.imageUrl ? (
                <div className="logo-display">
                  <img
                    src={logo.publicId ? getOptimizedImageUrl(logo.publicId, 'card') : logo.imageUrl}
                    alt={logo.altText}
                    className="logo-preview-image"
                  />
                  <div className="logo-info">
                    <p><strong>Alt Text:</strong> {logo.altText}</p>
                    {logo.width && logo.height && (
                      <p><strong>Dimensions:</strong> {logo.width} × {logo.height}px</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-logo-placeholder">
                  <i className="fas fa-image"></i>
                  <p>No logo uploaded</p>
                  <p className="placeholder-subtitle">Upload a logo to get started</p>
                </div>
              )}
            </div>
          </div>

          {/* Logo Management Actions */}
          <div className="logo-actions-section">
            <h3>Logo Actions</h3>
            
            <div className="action-buttons">
              <button
                className="btn btn-primary"
                onClick={openUploadWidget}
                disabled={isUploading || !cloudinaryLoaded}
              >
                {!cloudinaryLoaded ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading...
                  </>
                ) : isUploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload"></i>
                    {logo.imageUrl ? 'Replace Logo' : 'Upload Logo'}
                  </>
                )}
              </button>

              {logo.imageUrl && (
                <button
                  className="btn btn-danger"
                  onClick={removeLogo}
                >
                  <i className="fas fa-trash"></i>
                  Remove Logo
                </button>
              )}
            </div>

            {/* Alt Text Editor */}
            <div className="alt-text-editor">
              <label htmlFor="logo-alt-text">Alt Text (for accessibility)</label>
              <input
                type="text"
                id="logo-alt-text"
                value={logo.altText}
                onChange={(e) => updateAltText(e.target.value)}
                placeholder="Enter alt text for the logo"
                className="form-control"
              />
              <small className="form-help">
                This text will be used by screen readers and when the image cannot be displayed.
              </small>
            </div>

          </div>
        </div>

        {/* Logo Guidelines */}
        <div className="logo-guidelines">
          <h3>Logo Guidelines</h3>
          <div className="guidelines-content">
            <div className="guideline-item">
              <i className="fas fa-info-circle"></i>
              <div>
                <h4>Recommended Dimensions</h4>
                <p>Use a square image so the full mark fits. The crop is locked to 1:1.</p>
              </div>
            </div>
            <div className="guideline-item">
              <i className="fas fa-file-image"></i>
              <div>
                <h4>Supported Formats</h4>
                <p>PNG, JPG, JPEG, SVG, and WebP formats are supported</p>
              </div>
            </div>
            <div className="guideline-item">
              <i className="fas fa-palette"></i>
              <div>
                <h4>Design Tips</h4>
                <p>Use high contrast colors and ensure the logo is readable at small sizes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LogoManagement;

